import { useCompanionStore } from "@/stores/companion-store";
import type { ActiveExecution } from "@/stores/companion-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import type { ContextualStoryState } from "./contextual-story-state";
import type { ConversationMessage } from "@/stores/conversation-store";

export function contextualDiscoveryPresentationHeld(
  story: ContextualStoryState | null,
): boolean {
  return story?.basicsStage === "opening" || story?.basicsStage === "guide";
}

/** Native children have their own conversations; only root turns are held. */
export function contextualRootExecutionIds(
  story: ContextualStoryState | null,
  messages: ConversationMessage[],
  executions: Iterable<ActiveExecution> = [],
): string[] {
  if (!story) return [];
  const receipt = messages.find(
    (message) =>
      message.role === "user" &&
      !message.isOptimistic &&
      message.clientRequestId === story.requestId,
  );
  const conversationId = story.conversationId ?? receipt?.conversationId;
  return [
    ...new Set([
      ...(story.executionIds ?? []),
      story.executionId,
      receipt?.executionId,
      ...[...executions]
        .filter(
          (execution) =>
            conversationId &&
            execution.conversationId === conversationId &&
            execution.spaceId === story.projectId &&
            !execution.nativeSubagent,
        )
        .map((execution) => execution.id),
      ...messages
        .filter(
          (message) =>
            conversationId &&
            message.conversationId === conversationId &&
            message.role === "user" &&
            !message.isOptimistic,
        )
        .map((message) => message.executionId),
    ]),
  ].filter((id): id is string => !!id);
}

export function isContextualDiscoveryPresentationHeld(
  executionId: string | undefined,
): boolean {
  if (!executionId) return false;
  const state = useOnboardingStore.getState();
  const story = state.contextualStory;
  if (
    state.restarting ||
    state.step !== "desktop_usage_primer" ||
    !contextualDiscoveryPresentationHeld(story)
  )
    return false;
  if (
    contextualRootExecutionIds(
      story,
      useConversationStore.getState().messages,
    ).includes(executionId)
  )
    return true;
  const execution = useCompanionStore.getState().executions.get(executionId);
  return !!(
    story?.conversationId &&
    !execution?.nativeSubagent &&
    execution?.conversationId === story.conversationId &&
    execution.spaceId === story.projectId
  );
}

/** Bind before React effects, so the first running event cannot leak speech. */
export function bindContextualDiscoveryExecution(data: {
  execution_id?: unknown;
  client_request_id?: unknown;
  conversation_id?: unknown;
  project_id?: unknown;
  native_subagent_id?: unknown;
  parent_execution_id?: unknown;
}) {
  const state = useOnboardingStore.getState();
  const story = state.contextualStory;
  if (
    state.restarting ||
    state.step !== "desktop_usage_primer" ||
    !story ||
    !story.basicsStage ||
    !["looking", "live"].includes(story.phase) ||
    data.native_subagent_id ||
    data.parent_execution_id ||
    typeof data.execution_id !== "string" ||
    !data.execution_id ||
    (typeof data.project_id === "string" &&
      data.project_id !== story.projectId) ||
    (data.client_request_id !== story.requestId &&
      !(story.conversationId && data.conversation_id === story.conversationId))
  )
    return;
  if (
    story.executionId === data.execution_id &&
    story.executionIds?.includes(data.execution_id)
  )
    return;
  useOnboardingStore.setState({
    contextualStory: {
      ...story,
      executionId: story.executionIds?.includes(data.execution_id)
        ? story.executionId
        : data.execution_id,
      executionIds: [
        ...new Set([...(story.executionIds ?? []), data.execution_id]),
      ],
      conversationId:
        story.conversationId ??
        (typeof data.conversation_id === "string"
          ? data.conversation_id
          : null),
    },
  });
}

export function deferContextualQuestionSpeech(
  executionId: string | undefined,
  awaitingInputId: string,
): boolean {
  if (!isContextualDiscoveryPresentationHeld(executionId)) return false;
  const story = useOnboardingStore.getState().contextualStory!;
  if (!story.deferredQuestionSpeechIds?.includes(awaitingInputId))
    useOnboardingStore.setState({
      contextualStory: {
        ...story,
        deferredQuestionSpeechIds: [
          ...(story.deferredQuestionSpeechIds ?? []),
          awaitingInputId,
        ].slice(-16),
      },
    });
  return true;
}
